import { useState, useEffect } from 'react'
import { DateTime } from 'luxon'
import * as FA from 'react-icons/fa'
import AuthService from 'services/auth'
import CashFlow from 'services/CashFlow'
import { isCanceled, getApiErrorMessage, logApiError } from 'services/apiError'
import useAbortController from 'hooks/useAbortController'
import BankBalanceModal from 'pages/Modals/BankBalanceModal'
import { ReactComponent as Loading } from 'icons/loading2.svg'

import {
  ExpensesContainer,
  Section,
  SectionTitle,
  HintText,
  ActionButton,
  CardsRow,
  Card,
  CardLabel,
  CardMoney,
  CardHint,
  ExpenseRow,
  ExpenseInfo,
  ExpenseName,
  ExpenseMeta,
  IconButton,
  WarningText,
  MoneyText,
  LoadingContainer,
  InfoText
} from 'pages/Financial/Dre/styles'

const formatDate = isoDate => DateTime.fromISO(isoDate).toFormat('dd/MM/yyyy')
const tone = value => (value < 0 ? '#FF3939' : undefined)

export default function BankAccounts() {
  const businessId = AuthService.getBusinessId()
  const requestAuth = AuthService.getRequestAuth()
  const canEdit = AuthService.hasPermission('financial.cash_flow', 'edit')
  const { abortarPendentes } = useAbortController()

  const [loading, setLoading] = useState(true)
  const [errorMessage, setErrorMessage] = useState(null)
  const [current, setCurrent] = useState({ total: 0, accounts: [] })
  const [history, setHistory] = useState([])
  const [modalBalance, setModalBalance] = useState(null)
  const [modalAccountId, setModalAccountId] = useState(null)
  const [modalVisible, setModalVisible] = useState(false)

  useEffect(() => {
    getData()
  }, [])

  const getData = async () => {
    const signal = abortarPendentes()

    try {
      setLoading(true)
      setErrorMessage(null)

      const [currentResult, historyResult] = await Promise.all([
        CashFlow.getCurrentBalances({ businessId, requestAuth, signal }),
        CashFlow.getBalances({ businessId, requestAuth, signal })
      ])

      setCurrent(currentResult)
      setHistory(historyResult)
    } catch (error) {
      if (isCanceled(error)) return
      logApiError(error)
      setErrorMessage(getApiErrorMessage(error, 'Não foi possível carregar os saldos.'))
    } finally {
      if (!signal.aborted) setLoading(false)
    }
  }

  const deleteBalance = async balance => {
    try {
      setErrorMessage(null)
      await CashFlow.deleteBalance({ businessId, balanceId: balance.id, requestAuth })
      getData()
    } catch (error) {
      logApiError(error)
      setErrorMessage(getApiErrorMessage(error))
    }
  }

  const openModal = (balance, accountId = null) => {
    setModalBalance(balance)
    setModalAccountId(accountId)
    setModalVisible(true)
  }

  if (loading) {
    return (
      <LoadingContainer>
        <Loading/>
        <InfoText>Carregando saldos...</InfoText>
      </LoadingContainer>
    )
  }

  return (
    <ExpensesContainer>
      <Section>
        <SectionTitle>Contas bancárias</SectionTitle>
        <HintText>
          Os saldos são lançados por você: o último lançamento de cada conta é o saldo atual.
        </HintText>
        {current.accounts.length === 0 ? (
          <HintText>
            Nenhuma conta bancária cadastrada. Cadastre em Configurações &gt; Contas bancárias.
          </HintText>
        ) : (
          <>
            {canEdit && (
              <ActionButton type='button' onClick={() => openModal(null)}>Lançar saldo</ActionButton>
            )}
            <CardsRow>
              <Card>
                <CardLabel>Saldo total</CardLabel>
                <CardMoney value={current.total} tone={tone(current.total)}/>
                <CardHint>Soma do último saldo de cada conta</CardHint>
              </Card>
              {current.accounts.map(account => (
                <Card key={account.bankAccountId}>
                  <CardLabel>{account.bankName ? `${account.bankName} - ${account.description}` : account.description}</CardLabel>
                  {account.value === null ? (
                    <>
                      <CardHint>Sem saldo lançado</CardHint>
                      {canEdit && (
                        <IconButton
                          type='button'
                          aria-label={`Lançar primeiro saldo de ${account.description}`}
                          onClick={() => openModal(null, account.bankAccountId)}
                        >
                          <FA.FaPlus color='#252525' size={14}/>
                        </IconButton>
                      )}
                    </>
                  ) : (
                    <>
                      <CardMoney value={account.value} tone={tone(account.value)}/>
                      <CardHint>Em {formatDate(account.date)}</CardHint>
                    </>
                  )}
                </Card>
              ))}
            </CardsRow>
          </>
        )}
      </Section>
      {history.length > 0 && (
        <Section>
          <SectionTitle>Histórico de saldos</SectionTitle>
          {history.map(balance => (
            <ExpenseRow key={balance.id}>
              <ExpenseInfo>
                <ExpenseName>{balance.bank_account?.description}</ExpenseName>
                <ExpenseMeta>
                  {formatDate(balance.date)}
                  {balance.description ? ` · ${balance.description}` : ''}
                </ExpenseMeta>
              </ExpenseInfo>
              <MoneyText kind='total' value={balance.value}/>
              {canEdit && (
                <>
                  <IconButton type='button' aria-label='Editar saldo' onClick={() => openModal(balance)}>
                    <FA.FaPen color='#252525' size={14}/>
                  </IconButton>
                  <IconButton type='button' aria-label='Excluir saldo' onClick={() => deleteBalance(balance)}>
                    <FA.FaTrash color='#FF3939' size={14}/>
                  </IconButton>
                </>
              )}
            </ExpenseRow>
          ))}
        </Section>
      )}
      {errorMessage && <WarningText>{errorMessage}</WarningText>}
      <BankBalanceModal
        visible={modalVisible}
        balance={modalBalance}
        accounts={current.accounts}
        defaultAccountId={modalAccountId}
        onConfirm={() => getData()}
        onClose={() => setModalVisible(false)}
      />
    </ExpensesContainer>
  )
}
